import { createQuery, type QueryClient } from '@tanstack/solid-query'
import { boostingPacks } from '~/config'
import { api, getCurrentUserId } from '~/services/api'

/** Shape returned by `own-purchase-stats` when no `range` is supplied. */
export interface BoostingPackStats {
  month_count: number
  today_count: number
  total_count: number
}

const EMPTY_STATS: BoostingPackStats = {
  month_count: 0,
  today_count: 0,
  total_count: 0,
}

export const boostingPackStatsQueryKey = ['boosting-pack-stats'] as const

/**
 * Fetches a Boosting Pack's lifetime purchase stats (no `range` → includes
 * `total_count`) so the UI can enforce the "Limit 1 per person" rule.
 */
export const useBoostingPackStats = (productId: () => number) =>
  createQuery(() => ({
    queryKey: [...boostingPackStatsQueryKey, productId()],
    queryFn: async (): Promise<BoostingPackStats> => {
      const userId = await getCurrentUserId()
      const response = await api.service
        .get(`store/users/${userId}/product/${productId()}/own-purchase-stats`)
        .json<{ stats: BoostingPackStats | null }>()
      return response.stats ?? EMPTY_STATS
    },
    enabled: productId() > 0,
  }))

/** Optimistically marks a pack as purchased after a successful buy. */
export function markBoostingPackPurchased(
  queryClient: QueryClient,
  productId: number,
) {
  queryClient.setQueryData<BoostingPackStats>(
    [...boostingPackStatsQueryKey, productId],
    (old) => ({
      month_count: (old?.month_count ?? 0) + 1,
      today_count: (old?.today_count ?? 0) + 1,
      total_count: (old?.total_count ?? 0) + 1,
    }),
  )
}

/**
 * A Boosting Pack is "Limit 1 per person", so it counts as purchased once the
 * user has any lifetime purchase of it (`total_count > 0`).
 */
export function isPackPurchased(
  stats: BoostingPackStats | null | undefined,
): boolean {
  return (stats?.total_count ?? 0) > 0
}

/**
 * True only once every Boosting Pack is confirmed purchased. Packs whose
 * stats are still loading (or failed to load) count as not purchased, so
 * upsell surfaces default to showing while the answer is unknown.
 */
export function useAllBoostingPacksPurchased(): () => boolean {
  const queries = boostingPacks.map((pack) =>
    useBoostingPackStats(() => pack.productId),
  )
  return () => queries.every((query) => isPackPurchased(query.data))
}
